import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  CheckCircle2, AlertTriangle, FileX, ImageIcon, Sparkles, Loader2, UploadCloud, ScanFace,
} from 'lucide-react';
import { eventsApi, type FailedUploadItem, type FaceIndexErrorPhoto } from '../../api/events';
import { Button } from '../UI/Button';
import { MachhaCharacter } from '../../lib/machhaAsset';
import styles from './EventErrorsTab.module.css';

const ERROR_LABELS: Record<FailedUploadItem['errorCode'], string> = {
  size_exceeded:    'Too large',
  invalid_file:     'Invalid file',
  quota_exceeded:   'Quota reached',
  processing_error: 'Processing failed',
};

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

function formatRelativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface Props {
  eventId: string;
}

export const EventErrorsTab: React.FC<Props> = ({ eventId }) => {
  const [uploads, setUploads] = useState<FailedUploadItem[]>([]);
  const [faceErrors, setFaceErrors] = useState<FaceIndexErrorPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [uploadRes, faceRes] = await Promise.all([
        eventsApi.listUploadErrors(eventId),
        eventsApi.listFaceIndexErrors(eventId),
      ]);
      setUploads(uploadRes.items);
      setFaceErrors(faceRes.items);
    } catch {
      toast.error('Failed to load errors');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRetryFace = async () => {
    if (retrying || faceErrors.length === 0) return;
    setRetrying(true);
    try {
      const res = await eventsApi.retryFaceIndexErrors(eventId);
      toast.success(`Compressing & retrying ${res.total} photo${res.total === 1 ? '' : 's'} — running in background`);
      setTimeout(load, 4000);
      setTimeout(load, 12000);
    } catch {
      toast.error('Failed to start retry');
    } finally {
      setRetrying(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className={styles['center']}>
        <Loader2 className={styles['spinner']} />
        <p className={styles['loading-text']}>Running health check…</p>
      </div>
    );
  }

  const allClear = uploads.length === 0 && faceErrors.length === 0;

  // ── All clear ─────────────────────────────────────────────────────────────

  if (allClear) {
    return (
      <motion.div
        className={styles['all-clear-card']}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className={styles['all-clear-character']}>
          <MachhaCharacter state="Success" size={160} />
        </div>

        <h2 className={styles['all-clear-title']}>All systems clean</h2>
        <p className={styles['all-clear-sub']}>No issues to report for this event.</p>

        <div className={styles['all-clear-checks']}>
          <div className={styles['check-row']}>
            <span className={styles['check-icon-wrap']}>
              <CheckCircle2 className={styles['check-icon']} />
            </span>
            <div>
              <p className={styles['check-label']}>Upload pipeline</p>
              <p className={styles['check-desc']}>Every file from every camera was received cleanly</p>
            </div>
          </div>
          <div className={styles['check-divider']} />
          <div className={styles['check-row']}>
            <span className={styles['check-icon-wrap']}>
              <CheckCircle2 className={styles['check-icon']} />
            </span>
            <div>
              <p className={styles['check-label']}>Face indexing</p>
              <p className={styles['check-desc']}>Every photo was indexed successfully for face match</p>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Error view ────────────────────────────────────────────────────────────

  return (
    <div className={styles['root']}>
      {/* Summary strip */}
      <div className={styles['summary-strip']}>
        <StatusPill
          icon={UploadCloud}
          label="Upload failures"
          count={uploads.length}
        />
        <StatusPill
          icon={ScanFace}
          label="Face index errors"
          count={faceErrors.length}
        />
      </div>

      {/* Upload failures section */}
      {uploads.length > 0 && (
        <motion.section
          className={styles['section']}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className={styles['section-header']}>
            <div className={styles['section-header-left']}>
              <AlertTriangle className={styles['section-icon']} />
              <div>
                <h3 className={styles['section-title']}>
                  {uploads.length} failed upload{uploads.length !== 1 ? 's' : ''}
                </h3>
                <p className={styles['section-subtitle']}>
                  Received over FTP but could not be processed. Re-send the files from the camera.
                </p>
              </div>
            </div>
          </div>

          <ul className={styles['list']}>
            <AnimatePresence initial={false}>
              {uploads.map((item) => (
                <motion.li
                  key={item._id}
                  className={styles['row']}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16 }}
                >
                  <div className={styles['row-icon-wrap']}>
                    <FileX className={styles['row-icon']} />
                  </div>
                  <div className={styles['row-meta']}>
                    <p className={styles['row-filename']} title={item.filename}>{item.filename}</p>
                    <div className={styles['row-sub']}>
                      <span className={styles['chip']}>{ERROR_LABELS[item.errorCode]}</span>
                      <span className={styles['dot']}>·</span>
                      <span className={styles['row-message']}>{item.errorMessage}</span>
                      <span className={styles['dot']}>·</span>
                      <span className={styles['row-time']}>{formatRelativeTime(item.failedAt)}</span>
                    </div>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </motion.section>
      )}

      {/* Face index errors section */}
      {faceErrors.length > 0 && (
        <motion.section
          className={styles['section']}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: uploads.length > 0 ? 0.06 : 0 }}
        >
          <div className={styles['section-header']}>
            <div className={styles['section-header-left']}>
              <AlertTriangle className={styles['section-icon']} />
              <div>
                <h3 className={styles['section-title']}>
                  {faceErrors.length} photo{faceErrors.length !== 1 ? 's' : ''} too large for face indexing
                </h3>
                <p className={styles['section-subtitle']}>
                  Exceed AWS Rekognition's 5 MB limit. Recompress and retry — originals on S3 stay untouched.
                </p>
              </div>
            </div>
            <Button variant="primary" onClick={handleRetryFace} loading={retrying} disabled={retrying}>
              <Sparkles className={styles['btn-icon']} />
              Compress & retry all
            </Button>
          </div>

          <ul className={styles['list']}>
            <AnimatePresence initial={false}>
              {faceErrors.map((p) => (
                <motion.li
                  key={p._id}
                  className={styles['row']}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16 }}
                >
                  <div className={styles['thumb-wrap']}>
                    {p.thumbnailUrl ? (
                      <img src={p.thumbnailUrl} alt={p.originalFilename} className={styles['thumb']} loading="lazy" />
                    ) : (
                      <div className={styles['thumb-placeholder']}>
                        <ImageIcon className={styles['thumb-placeholder-icon']} />
                      </div>
                    )}
                  </div>
                  <div className={styles['row-meta']}>
                    <p className={styles['row-filename']} title={p.originalFilename}>{p.originalFilename}</p>
                    <div className={styles['row-sub']}>
                      <span className={styles['chip']}>Too large</span>
                      <span className={styles['dot']}>·</span>
                      <span>{formatSize(p.fileSizeBytes)}</span>
                    </div>
                  </div>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        </motion.section>
      )}

      {/* Passing sections shown as compact green pills */}
      {(uploads.length > 0 || faceErrors.length > 0) && (
        <div className={styles['passing-rows']}>
          {uploads.length === 0 && (
            <div className={styles['passing-row']}>
              <CheckCircle2 className={styles['passing-icon']} />
              <span>Upload pipeline — no failures</span>
            </div>
          )}
          {faceErrors.length === 0 && (
            <div className={styles['passing-row']}>
              <CheckCircle2 className={styles['passing-icon']} />
              <span>Face indexing — no errors</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── StatusPill ────────────────────────────────────────────────────────────────

function StatusPill({
  icon: Icon,
  label,
  count,
}: {
  icon: React.ElementType;
  label: string;
  count: number;
}) {
  const ok = count === 0;
  return (
    <div className={`${styles['status-pill']} ${ok ? styles['status-pill-ok'] : styles['status-pill-err']}`}>
      <Icon className={styles['status-pill-icon']} />
      <span className={styles['status-pill-label']}>{label}</span>
      {ok ? (
        <span className={styles['status-pill-badge-ok']}>Clean</span>
      ) : (
        <span className={styles['status-pill-badge-err']}>{count}</span>
      )}
    </div>
  );
}
